import {Typography, IconButton, Toolbar, AppBar, Tabs, Grid, Tab, Button} from '@mui/material';
import React, { useState, useEffect, Fragment } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from "react-i18next";
import {fromCircle} from 'ol/geom/Polygon';
import {useNavigate} from 'react-router';
import {getLength} from 'ol/sphere.js';
import {observer} from 'mobx-react';
import {transform} from 'ol/proj';
import FilterTime, {TL_NO_LIMIT, TL_TODAY, TL_TODAY_AND_YESTERDAY, TL_RANGE, TL_TODAY_AND_TWO_PREVIOUS_DATES} from './FilterTime.js';
import FilterOther, {BR_CODE_NOT_IN_USE, ECO_NOT_IN_USE, TWITCH_NOT_IN_USE} from './FilterOther.js';
import {dStr, unixNow, validDateStr, COUNTRY_ID_FI} from '../Util.js';
import {useGlobalStorage} from '../mobx/GlobalStorage.js';
import FilterPlace from './FilterPlace.js';
import FilterSpec from './FilterSpec.js';

export const defaultFilter = {
	time: {
		t_type: TL_TODAY,		/* Today */
		start_t: dStr(unixNow()),
		end_t: '',
	},
	country: null,
	cities: [],
	species: [],
	trips: [],
	asso: [],
	own_obs: true,
	no_coord: false,
	br_code_mask: BR_CODE_NOT_IN_USE,
	br_code: 10,
	eco: ECO_NOT_IN_USE,
	twitched: TWITCH_NOT_IN_USE,
	regions: [],
	coordinates: [],
};

const SearchDialog = observer((props) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	const navigate = useNavigate();

	const [startDate, setStartDate] = useState(dStr(Date.now() / 1000.0));
	const [startDateValid, setStartDateValid] = useState(true);
	const [endDate, setEndDate] = useState('');
	const [endDateValid, setEndDateValid] = useState(true);
	const [filter, setFilter] = useState(defaultFilter);
	const [features, setFeatures] = useState([]);

	useEffect(() => {
		if (gStorage.main.filter.active) {
// 			console.log('SearchDialog, useEffect, filter_active', gStorage.main.filter.filter.time.start_t, validDateStr(gStorage.main.filter.filter.time.start_t));
			let f = JSON.parse(JSON.stringify(gStorage.main.filter.filter));
			setFilter(f);

			if (validDateStr(gStorage.main.filter.filter.time.start_t)) {
				setStartDate(gStorage.main.filter.filter.time.start_t);
				setStartDateValid(validDateStr(gStorage.main.filter.filter.time.start_t));
			} else {
				setStartDate(dStr(Date.now() / 1000.0));
				setStartDateValid(true);
			}
			setEndDate(gStorage.main.filter.filter.time.end_t);
			if (gStorage.main.filter.filter.time.end_t.length) {
				setEndDateValid(validDateStr(gStorage.main.filter.filter.time.end_t));
			} else {
				setEndDateValid(true);
			}

			if (gStorage.main.filter.features) {
				setFeatures(gStorage.main.filter.features);
			}
		} else {
			let f = JSON.parse(JSON.stringify(defaultFilter));

			setFilter(f);

			setStartDate(dStr(Date.now() / 1000.0));
			setStartDateValid(true);

			setEndDate('');
			setEndDateValid(true);

			setFeatures([])
		}
		// eslint-disable-next-line
	}, []);

	/* ------------------------------------------------------------------ */

	const handleTabChange = (event, tab) => {
// 		console.log('handleTabChange', tab);
		gStorage.main.filter.setTab(tab);
	}

	const nolimitCheck = () => {
		let f = JSON.parse(JSON.stringify(filter));

		f.time.t_type = TL_NO_LIMIT;
		setFilter(f);
	}

	const todayCheck = () => {
		let f = JSON.parse(JSON.stringify(filter));

		f.time.t_type = TL_TODAY;
		setFilter(f);
	}

	const todayAndYesterdayCheck = () => {
		let f = JSON.parse(JSON.stringify(filter));

		f.time.t_type = TL_TODAY_AND_YESTERDAY;
		setFilter(f);
	}

	const todayAndTwoPreviousCheck = () => {
		let f = JSON.parse(JSON.stringify(filter));

		f.time.t_type = TL_TODAY_AND_TWO_PREVIOUS_DATES;
		setFilter(f);
	}

	const rangeCheck = () => {
		let f = JSON.parse(JSON.stringify(filter));

		f.time.t_type = TL_RANGE;
		setFilter(f);
	}

	const startDChange = (event, value) => {
		setStartDate(event.target.value);
		setStartDateValid(event.target.value.length && validDateStr(event.target.value));
	}

	const endDChange = (event, value) => {
		setEndDate(event.target.value);
		setEndDateValid(event.target.value.length && validDateStr(event.target.value));
	}

	function countryChange(event, value) {
		let f = JSON.parse(JSON.stringify(filter));
		const country = gStorage.lists.countrylist.find(c => c.id === event.target.value);

		if (country) {
			if (f.country) {
				/* Country => another country */
				if (f.country.id !== value.id) {
					f.cities = [];
					f.asso = [];
				}
			} else {
				/* No country => country other than Finland */
				if (value.id !== COUNTRY_ID_FI) {
					f.cities = [];
					f.asso = [];
				}
			}
		} else {
			if (f.country && f.country.id !== COUNTRY_ID_FI) {
				/* Country other than Finland => no country */
				f.cities = [];
			}
		}

		f.country = country;

		setFilter(f);
	}

	function cityChange(event, value) {
		let f = JSON.parse(JSON.stringify(filter));

		f.cities = value;
		setFilter(f);
	}

	function specChange(event, value) {
		let f = JSON.parse(JSON.stringify(filter));

		f.species = value;
		setFilter(f);
	}

	function tripChange(event, value) {
		let f = JSON.parse(JSON.stringify(filter));

		f.trips = value;
		setFilter(f);
	}

	function assoChange(event, value) {
		let f = JSON.parse(JSON.stringify(filter));

		f.asso = value;
		setFilter(f);
	}

	function brCodeMaskChange(event, value) {
		let f = JSON.parse(JSON.stringify(filter));

		f.br_code_mask = event.target.value;
		setFilter(f);
	}

	function brCodeChange(event, value) {
		let f = JSON.parse(JSON.stringify(filter));

		f.br_code = event.target.value;
		setFilter(f);
	}

	function ownCheck(event) {
		let f = JSON.parse(JSON.stringify(filter));

		f.own_obs = !f.own_obs;
		setFilter(f);
	}

	function noCoordCheck(event) {
		let f = JSON.parse(JSON.stringify(filter));

		f.no_coord = !f.no_coord;
		setFilter(f);
	}

	function ecoChange(event) {
		let f = JSON.parse(JSON.stringify(filter));

		f.eco = event.target.value;
		setFilter(f);
	}

	function twitchChange(event) {
		let f = JSON.parse(JSON.stringify(filter));

		f.twitched = event.target.value;
		setFilter(f);
	}

	function setCoordinates(c) {
		setFeatures(c);
	}

	function clearSel() {
		setFeatures([]);
	}

	function searchApply() {
		let f = JSON.parse(JSON.stringify(filter));
		let e = unixNow();

		f.coordinates = [];

		features.forEach((ff) => {
			const geom = ff.getGeometry();
			const type = geom.getType();

			if (type === 'Polygon') {
				let c = geom.getCoordinates()[0];

				c.forEach((cc, ind) => {
					c[ind] = transform(cc, 'EPSG:3857', 'EPSG:4326');
					c[ind][0] = Math.floor(c[ind][0] * 1000000);
					c[ind][1] = Math.floor(c[ind][1] * 1000000);
				});

				f.coordinates.push({type: type, coord: c});
			} else if (type === 'Circle') {
				const poly = fromCircle(geom);
				const length = getLength(poly);
				const radius = Math.round(length / (2 * Math.PI));

				let c = transform(geom.getCenter(), 'EPSG:3857', 'EPSG:4326');
				c[0] = Math.floor(c[0] * 1000000);
				c[1] = Math.floor(c[1] * 1000000);

				f.coordinates.push({type: type, center: c, radius: radius});
			}
		});
		
		gStorage.main.filter.setFeatures(features);

		f.time.end_t = '';

		if (f.time.t_type === TL_NO_LIMIT) {
			f.time.start_t = '';
		} else if (f.time.t_type === TL_TODAY) {
			f.time.start_t = dStr(e);
		} else if (f.time.t_type === TL_TODAY_AND_YESTERDAY) {
			f.time.start_t = dStr(e - (24 * 3600));
		} else if (f.time.t_type === TL_TODAY_AND_TWO_PREVIOUS_DATES) {
			f.time.start_t = dStr(e - (48 * 3600));
		} else if (f.time.t_type === TL_RANGE) {
			f.time.start_t = startDate;
			f.time.end_t = endDate;
		}

		gStorage.main.filter.setFilter(f);

		gStorage.main.filter.setActive(true);
		gStorage.main.filter.setChanged(true);

		gStorage.main.sorting.setPrimarySorting(0, 0);

		navigate('/', { replace: true });
	}

	function searchDefaults() {
		let f = JSON.parse(JSON.stringify(defaultFilter));

		f.time.start_t = dStr(Date.now() / 1000.0);
		setFilter(f);

		setFeatures([]);
	}

	function searchValid() {
		if (filter.time.t_type === TL_RANGE && (!startDateValid || !endDateValid))
			return false;

		return true;
	}

	function searchBack() {
		navigate('/', { replace: true });
	}

	return (
		<Fragment>
			<AppBar position="sticky" id="appbar">
				<Toolbar variant="dense">
					<Typography variant="h6" style={{flexGrow: 1, textAlign: 'center'}}>{t('menu.filter')}</Typography>
					<IconButton edge="start" color="inherit" onClick={searchBack} aria-label="close">
						<CloseIcon />
					</IconButton>
				</Toolbar>
			</AppBar>

        		<Grid container direction="row" justifyContent="center" alignItems="center" spacing={0}>
				<Grid>
					<Tabs
						id="searchtab"
						value={gStorage.main.filter.tab} 
						onChange={handleTabChange} 
						indicatorColor="primary"
						textColor="primary"
						scrollButtons="auto"
						variant="scrollable"
						sx={{mb: 1}}>
						<Tab label={t('filter.time')}/>
						<Tab label={t('filter.place')}/>
						<Tab label={t('filter.species')}/>
						<Tab label={t('filter.other')}/>
					</Tabs>
				</Grid>

				<Grid size={{xs: 12}} align={'center'}>
					<FilterTime 
						filter={filter}
						setFilter={setFilter}
						nolimitCheck={nolimitCheck}
						todayCheck={todayCheck}
						todayAndYesterdayCheck={todayAndYesterdayCheck}
						todayAndTwoPreviousCheck={todayAndTwoPreviousCheck}
						rangeCheck={rangeCheck}
						startDate={startDate}
						startDChange={startDChange}
						startDateValid={startDateValid}
						endDate={endDate}
						endDChange={endDChange}
						endDateValid={endDateValid}
						/>
				</Grid>

				<Grid size={{xs: 12}} align={'center'}>
					<FilterPlace 
						filter={filter}
						features={features}
						countryChange={countryChange}
						cityChange={cityChange}
						assoChange={assoChange}
						setCoordinates={setCoordinates}
						clearSel={clearSel}
						/>
				</Grid>

				<Grid size={{xs: 12}} align={'center'}>
					<FilterSpec
						filter={filter}
						setFilter={setFilter}
						specChange={specChange}
						/>
				</Grid>
				
				<Grid size={{xs: 12}} align={'center'}>
					<FilterOther
						filter={filter}
						setFilter={setFilter}
						tripChange={tripChange}
						brCodeMaskChange={brCodeMaskChange}
						brCodeChange={brCodeChange}
						ownCheck={ownCheck}
						noCoordCheck={noCoordCheck}
						ecoChange={ecoChange}
						twitchChange={twitchChange}
						/>
				</Grid>

				<Grid size={{xs: 12}} align={'center'} id="bottomg">
					<Button
						variant="contained" 
						color="primary" 
						size="small" 
						style={{width:143, margin: 4}} 
						onClick={searchApply}
						disabled={!(searchValid())}>
						{t('filter.apply')}
					</Button>
					<Button
						variant="contained" 
						color="primary" 
						size="small" 
						style={{width:143, margin: 4}} 
						onClick={searchDefaults}>
						{t('filter.defaults')}
					</Button>
				</Grid>
			</Grid>
		</Fragment>
	);
});

export default SearchDialog;
